/**
 * ExportSkillDialog — confirm + trigger the skill export flow.
 *
 * Surfaces format / target / redaction toggles and shows a one-line
 * preview of the resulting artifact once the export completes. The user
 * can copy the rendered text to clipboard or download a `.md` / `.json`
 * / `.yaml` file directly. Founder + tuner only — non-writers don't see
 * the trigger.
 */
import type {
  ExportSkillRequest,
  ExportSkillResponse,
  SkillExportFormat,
  SkillExportTarget,
} from '@chaos/types/learning';
import { useMutation } from '@tanstack/react-query';
import { Copy, Download, Loader2 } from 'lucide-react';
import * as React from 'react';

import { learningApi } from '../../lib/api/learning';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { useToaster } from '../ui/toast';

const FORMATS: ReadonlyArray<{
  value: SkillExportFormat;
  label: string;
  ext: string;
  mime: string;
}> = [
  { value: 'anthropic_skill_md', label: 'Anthropic SKILL.md', ext: 'md', mime: 'text/markdown' },
  { value: 'codex_json', label: 'Codex JSON', ext: 'json', mime: 'application/json' },
  { value: 'gemini_yaml', label: 'Gemini YAML', ext: 'yaml', mime: 'text/yaml' },
  { value: 'generic_json', label: 'Generic JSON', ext: 'json', mime: 'application/json' },
];

const TARGETS: ReadonlyArray<{ value: SkillExportTarget; label: string }> = [
  { value: 'internal', label: 'Internal' },
  { value: 'external', label: 'External' },
  { value: 'sold', label: 'Sold' },
  { value: 'public_marketplace', label: 'Public marketplace' },
];

interface ExportSkillDialogProps {
  skillId: string;
  skillName: string;
  skillKey: string;
  version: string;
  trigger?: React.ReactNode;
  defaultOpen?: boolean;
  onClose?: () => void;
}

export function ExportSkillDialog({
  skillId,
  skillName,
  skillKey,
  version,
  trigger,
  defaultOpen,
  onClose,
}: ExportSkillDialogProps) {
  const [open, setOpen] = React.useState(defaultOpen ?? false);
  const [format, setFormat] = React.useState<SkillExportFormat>('anthropic_skill_md');
  const [target, setTarget] = React.useState<SkillExportTarget>('internal');
  const [result, setResult] = React.useState<ExportSkillResponse | null>(null);
  const { toast } = useToaster();

  const mutation = useMutation({
    mutationFn: (req: ExportSkillRequest) => learningApi.exportSkill(req),
    onSuccess: (data) => {
      setResult(data);
      toast({
        title: 'Skill exported',
        description: `${FORMATS.find((f) => f.value === format)?.label ?? format} · ${data.skill_version}`,
      });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Export failed.';
      toast({ title: 'Export failed', description: message, variant: 'destructive' });
    },
  });

  function handleClose(next: boolean) {
    setOpen(next);
    if (!next) {
      setResult(null);
      mutation.reset();
      onClose?.();
    }
  }

  function handleExport() {
    mutation.mutate({
      skill_id: skillId,
      version,
      format,
      target,
      persist: true,
    });
  }

  function handleDownload() {
    if (!result) return;
    const meta = FORMATS.find((f) => f.value === result.format);
    const ext = meta?.ext ?? 'txt';
    const mime = meta?.mime ?? 'text/plain';
    const blob = new Blob([result.artifact_text], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${skillKey}-${result.skill_version}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleCopy() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.artifact_text);
      toast({ title: 'Copied to clipboard' });
    } catch (err) {
      toast({
        title: 'Copy failed',
        description: err instanceof Error ? err.message : 'Clipboard unavailable',
        variant: 'destructive',
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Export skill</DialogTitle>
          <DialogDescription>
            {skillName} · v{version}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="export-format">Format</Label>
              <select
                id="export-format"
                value={format}
                onChange={(e) => setFormat(e.target.value as SkillExportFormat)}
                className={cn(
                  'h-9 rounded border border-border bg-background px-2 text-[13px]',
                  'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
                )}
              >
                {FORMATS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="export-target">Target</Label>
              <select
                id="export-target"
                value={target}
                onChange={(e) => setTarget(e.target.value as SkillExportTarget)}
                className={cn(
                  'h-9 rounded border border-border bg-background px-2 text-[13px]',
                  'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
                )}
              >
                {TARGETS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {result ? (
            <div className="flex flex-col gap-2 rounded border border-border bg-muted/30 p-3">
              <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-muted-foreground">
                <span>{FORMATS.find((f) => f.value === result.format)?.label}</span>
                <span className="font-mono normal-case">
                  hash: {result.artifact_hash.slice(0, 12)}…
                  {result.redaction_total_replacements > 0 && (
                    <span className="ml-2 text-warning">
                      redactions: {result.redaction_total_replacements}
                    </span>
                  )}
                </span>
              </div>
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded bg-background p-2 font-mono text-[11px] text-foreground/80">
                {result.artifact_text.slice(0, 1200)}
                {result.artifact_text.length > 1200 ? '\n…' : ''}
              </pre>
            </div>
          ) : null}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {result ? (
            <>
              <Button type="button" variant="outline" onClick={handleCopy}>
                <Copy className="h-3.5 w-3.5" aria-hidden /> Copy
              </Button>
              <Button type="button" onClick={handleDownload}>
                <Download className="h-3.5 w-3.5" aria-hidden /> Download
              </Button>
            </>
          ) : (
            <Button type="button" onClick={handleExport} disabled={mutation.isPending}>
              {mutation.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Exporting…
                </>
              ) : (
                <>
                  <Download className="h-3.5 w-3.5" aria-hidden /> Export
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
